/**
 * Profile avatar: a small, fully data-driven upper-body portrait. The config is
 * just a set of indices into the part/colour tables in `parts.ts`, so it
 * serialises to a tiny string and renders to an SVG with no external assets.
 */
import * as P from './parts';

/**
 * Master switch for the profile-avatar UI. While false, the editor entry points
 * (HUD button, lobby thumbnail, waiting-room seat portraits) are hidden; the
 * model/render/sync code stays intact so flipping this back to true re-enables
 * the whole feature.
 */
export const AVATAR_ENABLED = false;

export interface AvatarConfig {
  bg: number;
  skin: number;
  faceShape: number;
  hair: number;
  hairColor: number;
  brows: number;
  eyes: number;
  nose: number;
  mouth: number;
  clothing: number;
  clothingColor: number;
}

/** Editable categories, in the order the editor shows them. */
export const AVATAR_CATEGORIES = [
  { key: 'faceShape', label: 'avatar.face', count: P.FACE_COUNT },
  { key: 'hair', label: 'avatar.hair', count: P.HAIR_COUNT },
  { key: 'hairColor', label: 'avatar.hairColor', count: P.HAIR.length, swatch: P.HAIR },
  { key: 'brows', label: 'avatar.brows', count: P.BROW_COUNT },
  { key: 'eyes', label: 'avatar.eyes', count: P.EYE_COUNT },
  { key: 'nose', label: 'avatar.nose', count: P.NOSE_COUNT },
  { key: 'mouth', label: 'avatar.mouth', count: P.MOUTH_COUNT },
  { key: 'clothing', label: 'avatar.clothing', count: P.CLOTHING_COUNT },
  { key: 'clothingColor', label: 'avatar.clothingColor', count: P.CLOTH.length, swatch: P.CLOTH },
  { key: 'skin', label: 'avatar.skin', count: P.SKIN.length, swatch: P.SKIN },
  { key: 'bg', label: 'avatar.bg', count: P.BG.length, swatch: P.BG },
] as const satisfies ReadonlyArray<{ key: keyof AvatarConfig; label: string; count: number; swatch?: readonly string[] }>;

export const DEFAULT_AVATAR: AvatarConfig = {
  bg: 0,
  skin: 1,
  faceShape: 0,
  hair: 1,
  hairColor: 0,
  brows: 0,
  eyes: 0,
  nose: 0,
  mouth: 0,
  clothing: 0,
  clothingColor: 0,
};

const KEYS = Object.keys(DEFAULT_AVATAR) as (keyof AvatarConfig)[];

/** Compact, order-stable serialization (e.g. "0.1.0.1.0.0.0.0.0.0.0"). */
export function serializeAvatar(a: AvatarConfig): string {
  return KEYS.map((k) => a[k] | 0).join('.');
}

/** Parse a serialized avatar; missing/garbage fields fall back to the default. */
export function parseAvatar(s: string | null | undefined): AvatarConfig {
  if (!s) return { ...DEFAULT_AVATAR };
  const parts = s.split('.').map((n) => parseInt(n, 10));
  const out = { ...DEFAULT_AVATAR };
  KEYS.forEach((k, i) => {
    if (Number.isFinite(parts[i]) && parts[i] >= 0) out[k] = parts[i];
  });
  return out;
}

const rint = (n: number) => Math.floor(Math.random() * n);

/** A random, sensible avatar (bald is rare; everything else uniform). */
export function randomAvatar(): AvatarConfig {
  return {
    bg: rint(P.BG.length),
    skin: rint(P.SKIN.length),
    faceShape: rint(P.FACE_COUNT),
    hair: Math.random() < 0.08 ? 0 : 1 + rint(P.HAIR_COUNT - 1),
    hairColor: rint(P.HAIR.length),
    brows: rint(P.BROW_COUNT),
    eyes: rint(P.EYE_COUNT),
    nose: rint(P.NOSE_COUNT),
    mouth: rint(P.MOUTH_COUNT),
    clothing: rint(P.CLOTHING_COUNT),
    clothingColor: rint(P.CLOTH.length),
  };
}

/**
 * Render the avatar to SVG markup. Layer order (back→front): background,
 * clothing, neck, hair-back, face, brows, eyes, nose, mouth, hair-front.
 */
export function renderAvatarSvg(a: AvatarConfig, opts: { bg?: boolean } = {}): string {
  const h = P.hair(a.hair, a.hairColor);
  const showBg = opts.bg !== false;
  const inner =
    (showBg ? P.background(a.bg) : '') +
    P.clothing(a.clothing, a.clothingColor) +
    P.neck(a.skin) +
    h.back +
    P.face(a.faceShape, a.skin) +
    P.brows(a.brows, a.hairColor) +
    P.eyes(a.eyes) +
    P.nose(a.nose, a.skin) +
    P.mouth(a.mouth) +
    h.front;
  return `<svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" class="avatar-svg">${inner}</svg>`;
}

/** Render into an existing element (sets innerHTML to the SVG). */
export function paintAvatar(el: HTMLElement, a: AvatarConfig, opts?: { bg?: boolean }): void {
  el.innerHTML = renderAvatarSvg(a, opts);
}
